import type { ComponentChildren, ComponentType } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';

import { activeTab, HUBS, hubTabs, navLabel, type HubId } from './navigation.js';
import { HubContext } from './pageContext.js';

// Samengevoegde pagina's (WP2, docs/ux/compact-plan.md hoofdstuk 3): Samen, Bord en Gezin (en elke
// hub met `wrapped` in lib/navigation.ts). Eén paginakop met de titel van de hub en daaronder de
// tabbladen (links naar de eigen, bestaande adressen). De schermen zelf blijven gewoon hun
// PageHeader tekenen; binnen een hub maakt lib/ui.tsx daar de kop van de hub van.
//
// Gebruik (App.tsx):
//   <Route path="/kamers" component={guard(hubbed('bord', RoomsPage), { module: 'rooms' })} />
// Twee schermen onder één tabblad: <HubSection> om het tweede; dat krijgt een sectiekop.

/**
 * Tabbladen van een hub als navigatie (links met aria-current), in de stijl van Tabs uit
 * @amumin/ui: tekst met een streep, op een smal scherm vegen met een zachte rand.
 */
export function HubTabs({ id }: { id: HubId }) {
  const { path } = useLocation();
  const tabs = hubTabs(id);
  const active = activeTab(id, path);
  const listRef = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState(false);
  const updateFade = () => {
    const el = listRef.current;
    if (!el) return;
    // In het Arabisch loopt scrollLeft negatief; de afstand tot het eind is in beide richtingen gelijk.
    const next = Math.abs(el.scrollLeft) + el.clientWidth < el.scrollWidth - 4;
    if (next !== fade) setFade(next);
  };
  useEffect(() => {
    const el = listRef.current;
    const tab = el?.querySelector<HTMLElement>('[aria-current="page"]');
    if (el && tab && typeof tab.scrollIntoView === 'function') {
      tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    updateFade();
  }, [path, tabs.length]);
  useEffect(() => {
    window.addEventListener('resize', updateFade);
    return () => window.removeEventListener('resize', updateFade);
  });
  return (
    <nav class="hub-tabs" aria-label={navLabel(HUBS[id].title)} data-name={`hub-${id}`}>
      <div class="hub-tabs-list" ref={listRef} onScroll={updateFade}>
        {tabs.map((t) => (
          <a
            key={t.href}
            href={t.href}
            class="hub-tab"
            aria-current={active?.href === t.href ? 'page' : undefined}
            data-name={`hub-tab-${t.key}`}
          >
            <span>{navLabel(t.key)}</span>
          </a>
        ))}
      </div>
      {fade ? <span class="hub-tabs-fade" aria-hidden="true" /> : null}
    </nav>
  );
}

/** Een scherm als tabblad van een hub: dezelfde kop (titel van de hub) met de tabbladen. */
export function hubbed(id: HubId, Page: ComponentType): ComponentType {
  return function HubPage() {
    return (
      <HubContext.Provider value={{ id, mode: 'page' }}>
        <Page />
      </HubContext.Provider>
    );
  };
}

/**
 * Een tweede scherm onder hetzelfde tabblad (Sterren: Goedkeuren en Beloningen): zijn PageHeader
 * wordt een sectiekop (h2) met dezelfde knoppen.
 */
export function HubSection({ id, children }: { id?: HubId; children: ComponentChildren }) {
  return (
    <section class="hub-section">
      <HubContext.Provider value={{ id, mode: 'section' }}>{children}</HubContext.Provider>
    </section>
  );
}
